import { Badge, Button, IconButton, Modal } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArticleCategory, TemperatureClass } from '@warehouse/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../../auth/auth-store';
import { useConfirm } from '../../../components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { createCategory, listCategories, removeCategory, updateCategory, type CategoryListRow } from '../api';
import { conflictDetails } from '../components/BarcodeConflict';
import { SelectField, TextField } from '../components/form';
import { toastMessageError } from '../notify';

const TEMPERATURE_TONES: Record<TemperatureClass, 'neutral' | 'info' | 'brand' | 'success'> = {
  dry: 'neutral',
  chilled: 'info',
  frozen: 'brand',
  produce: 'success',
};

/**
 * Article categories. `bcName` is surfaced as a first-class column because it
 * is the join key BC item sync matches on — the old WMS hid it and a mismatch
 * degraded sync to a logged warning nobody read.
 */
export function CategoriesPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { state, setState } = useListState({ defaultSort: 'sort', defaultDir: 'asc' });
  const canEdit = usePermission('registers.articles.categories');
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<CategoryListRow | 'new' | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['master-data', 'categories', listQueryKey(state)],
    queryFn: () => listCategories(state),
  });
  useLiveQuery(['articles'], ['master-data', 'categories']);

  const remove = useMutation({
    mutationFn: (id: string) => removeCategory(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:categories.removed'));
    },
    onError: (err: unknown) => {
      const conflict = conflictDetails(err);
      if (conflict?.reason === 'category_in_use') {
        toastMessageError(
          t('masterData:categories.inUse', { articles: conflict.params['articles'] ?? 0 }),
        );
        return;
      }
      toastError(err);
    },
  });

  const columns: DataTableColumn<CategoryListRow>[] = [
    { id: 'name', header: t('masterData:categories.columns.name'), sortField: 'name', cell: (row) => row.name },
    {
      id: 'bcName',
      header: t('masterData:categories.columns.bcName'),
      sortField: 'bcName',
      cell: (row) => (row.bcName ? <span className="font-mono text-xs">{row.bcName}</span> : '—'),
    },
    {
      id: 'temperature',
      header: t('masterData:categories.columns.temperatureClass'),
      cell: (row) =>
        row.temperatureClass ? (
          <Badge tone={TEMPERATURE_TONES[row.temperatureClass]}>
            {t(`masterData:categories.temperature.${row.temperatureClass}`)}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'sort',
      header: t('masterData:categories.columns.sort'),
      sortField: 'sort',
      align: 'right',
      hideBelow: 'md',
      cell: (row) => row.sort,
    },
    {
      id: 'articles',
      header: t('masterData:categories.columns.articles'),
      align: 'right',
      hideBelow: 'md',
      cell: (row) => row.articleCount,
    },
    {
      id: 'actions',
      header: '',
      width: 'w-24',
      align: 'right',
      cell: (row) =>
        canEdit ? (
          <div className="flex justify-end gap-1">
            <IconButton
              aria-label={t('common:actions.edit')}
              onClick={() => {
                setEditing(row);
              }}
            >
              <Pencil className="size-4" />
            </IconButton>
            <IconButton
              variant="danger"
              aria-label={t('common:actions.delete')}
              onClick={() => {
                void (async () => {
                  const ok = await confirm({
                    title: t('masterData:categories.confirmRemove.title'),
                    message: t('masterData:categories.confirmRemove.message', { name: row.name }),
                  });
                  if (ok) remove.mutate(row.id);
                })();
              }}
            >
              <Trash2 className="size-4" />
            </IconButton>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      {dialog}
      <PageHeader
        title={t('masterData:categories.title')}
        subtitle={t('masterData:categories.subtitle')}
        actions={
          canEdit ? (
            <Button
              icon={<Plus />}
              onClick={() => {
                setEditing('new');
              }}
            >
              {t('masterData:categories.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isLoading}
        error={error}
        searchPlaceholder={t('masterData:categories.searchPlaceholder')}
        emptyTitle={t('masterData:categories.empty')}
        emptyDescription={t('masterData:categories.emptyDescription')}
      />

      {editing ? (
        <CategoryModal
          category={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}

interface CategoryFormValues {
  name: string;
  bcName: string;
  temperatureClass: string;
  sort: string;
}

function CategoryModal({
  category,
  onClose,
}: {
  category: ArticleCategory | null;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState } = useForm<CategoryFormValues>({
    defaultValues: {
      name: category?.name ?? '',
      bcName: category?.bcName ?? '',
      temperatureClass: category?.temperatureClass ?? '',
      sort: String(category?.sort ?? 0),
    },
  });

  const mutation = useMutation({
    mutationFn: (values: CategoryFormValues) => {
      const body = {
        name: values.name.trim(),
        bcName: values.bcName.trim() || null,
        temperatureClass: values.temperatureClass || null,
        sort: Number(values.sort || '0'),
      };
      return category ? updateCategory(category.id, body) : createCategory(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(category ? t('masterData:categories.saved') : t('masterData:categories.created'));
      onClose();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={category ? t('masterData:categories.edit') : t('masterData:categories.new')}
      width={520}
    >
      <form
        onSubmit={(event) => {
          void handleSubmit((values) => {
            mutation.mutate(values);
          })(event);
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <TextField
          label={t('masterData:categories.fields.name')}
          autoFocus
          error={formState.errors.name ? t('common:common.required') : undefined}
          {...register('name', { required: true })}
        />
        <TextField
          label={t('masterData:categories.fields.bcName')}
          hint={t('masterData:categories.hints.bcName')}
          {...register('bcName')}
        />
        <SelectField
          label={t('masterData:categories.fields.temperatureClass')}
          hint={t('masterData:categories.hints.temperatureClass')}
          options={[
            { value: '', label: t('masterData:categories.temperature.none') },
            { value: 'dry', label: t('masterData:categories.temperature.dry') },
            { value: 'chilled', label: t('masterData:categories.temperature.chilled') },
            { value: 'frozen', label: t('masterData:categories.temperature.frozen') },
            { value: 'produce', label: t('masterData:categories.temperature.produce') },
          ]}
          {...register('temperatureClass')}
        />
        <TextField
          label={t('masterData:categories.fields.sort')}
          type="number"
          hint={t('masterData:categories.hints.sort')}
          {...register('sort')}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            {t('common:actions.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
